import { JSX, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { getEmployeeOnboardingSchema } from './schema';
import EmployeeOnboardingStepOne from '../../views/EmployeeOnboardingStepOne';
import EmployeeOnboardingStepTwo from '../../views/EmployeeOnboardingStepTwo';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import {
  Department,
  GetAllDepartmentResponse,
  GetAllRolesResponse,
  Roles,
} from '@EHSNavigator/backend';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import NoDepartment from '../../components/NoDepartment';
import Loader from '../../components/Loader';
import { hideBodyScroll, showBodyScroll } from '../../utils/bodyScroll';
import { useLocalStorage } from '@uidotdev/usehooks';
import { UserWithOrgContext } from '../../types/user';

const INITIAL_STATE = {
  employeeId: '',
  fullName: '',
  email: '',
  department: '',
  jobTitle: '',
  role: '',
  contact: '',
  dob: '',
  dateOfHire: '',
};

export default function EmployeeOnboardingPage() {
  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;
  const [currentStep, setCurrentStep] = useState<number>(1);
  const dispatch = useDispatch();
  const { UserApi, DepartmentApi, RolesApi, AuthApi } = useApiClients();

  const {
    data: allDepartments,
    isLoading: departmentsLoading,
    isRefetching,
  } = useQuery<GetAllDepartmentResponse>({
    queryKey: ['departments'],
    queryFn: async () => {
      const response =
        await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
          ...(organizationId ? { organizationId } : {}),
        });
      return response?.data;
    },
  });
  const { data: allRoles, isLoading: rolesLoading } =
    useQuery<GetAllRolesResponse>({
      queryKey: ['roles'],
      queryFn: async () => {
        const response = await RolesApi.rolesControllerGetAllRoles();
        return response?.data;
      },
    });
  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const roles =
    allRoles?.roles?.map((node: Roles) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const employeeOnboardingHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getEmployeeOnboardingSchema(currentStep),
    onSubmit: async (values) => {
      if (currentStep === 2) {
        try {
          const response = await UserApi?.userControllerCreate({
            createUserDTOAdmin: {
              employeeId: values.employeeId,
              fullName: values.fullName,
              email: values.email,
              departmentId: values.department,
              jobTitle: values.jobTitle,
              roleId: values.role,
              contact: values.contact ? String(values.contact) : undefined,
              dob: moment(values?.dob).format('DD/MM/YYYY'),
              dateOfHire: moment(values?.dateOfHire).format('DD/MM/YYYY'),
              ...(organizationId ? { organizationId } : {}),
            },
          });
          toast.success(response?.data?.message);
          setCurrentStep(1);
          employeeOnboardingHandler?.resetForm();
          dispatch(toggleModal(''));
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((msg: string) => toast.error(msg));
          } else {
            toast.error(errorMessage || 'An unexpected error occurred.');
          }
        }
      }
    },
  });

  const isEmployeeOnBoardingLoading = employeeOnboardingHandler.isSubmitting;

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      window.scrollTo(0, 0);
      setCurrentStep(steps);
    } else {
      const errors = await employeeOnboardingHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        const res = await AuthApi.authControllerCheckEmailExist({
          checkEmailExistDTO: {
            email: employeeOnboardingHandler?.values?.email,
          },
        });
        if (res?.data.exist === false) {
          setCurrentStep(steps);
          window.scrollTo(0, 0);
        } else {
          employeeOnboardingHandler?.setFieldError(
            'email',
            'Email already exists',
          );
        }
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});
        employeeOnboardingHandler.setTouched(touchedFields);
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (departments?.length === 0) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    };

    handleScroll();
    return () => {
      showBodyScroll();
    };
  }, [departments]);

  const steps: Record<number, JSX.Element> = {
    1: (
      <EmployeeOnboardingStepOne
        formHandler={employeeOnboardingHandler}
        handleSteps={handleNextStep}
        departments={departments}
        roles={roles}
      />
    ),
    2: (
      <EmployeeOnboardingStepTwo
        formHandler={employeeOnboardingHandler}
        handleSteps={handleNextStep}
        departments={departments}
        roles={roles}
        isLoading={isEmployeeOnBoardingLoading}
      />
    ),
  };

  if (departmentsLoading || rolesLoading || isRefetching) {
    return <Loader />;
  }

  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action="#"
        className="flex flex-1 flex-col"
        onSubmit={employeeOnboardingHandler?.handleSubmit}
      >
        {steps[currentStep]}
      </form>
      {departments?.length === 0 && <NoDepartment />}
    </div>
  );
}
